import React, { FC, ReactNode, useCallback } from 'react'

import * as DropdownMenu from '@radix-ui/react-dropdown-menu'
import * as Separator from '@radix-ui/react-separator'

import { CheckCircle } from 'react-bootstrap-icons'
import { ConditionalWrapper } from '../ConditionalWrapper'

import './Dropdown.scss'

export type Option = {
  type: 'checkbox' | 'radio' | 'button' | 'label' | 'link' | 'node',
  id: string
  label?: string,
  icon?: ReactNode,
  checked?: boolean,
  disabled?: boolean,
  onSelect?: (option: Option) => void,
  to?: string,
  content?: React.ReactNode
}

export type OptionGroup = {
  options: Option[]
  type?: 'radio' | 'default'
  value?: string
}

export interface DropdownProps {
  optionGroups: OptionGroup[]
  trigger: React.ReactNode
  open?: boolean
  contentProps?: DropdownMenu.DropdownMenuContentProps
  itemProps?: DropdownMenu.DropdownMenuItemProps
  arrow?: boolean
  disabled?: boolean
  onOpenChange?: (open: boolean) => any
}

export const Dropdown: FC<DropdownProps> = ({
  optionGroups,
  trigger,
  open,
  contentProps,
  itemProps,
  arrow = true,
  disabled = false,
  onOpenChange = (open: boolean) => undefined
}) => {
  const renderItem = useCallback((o: Option) => {
    switch (o.type) {
      case 'checkbox':
        return (
          <DropdownMenu.CheckboxItem
            key={o.id}
            className="poll-select-item"
            onSelect={(e: Event) => {
              e.stopPropagation()
              o.onSelect && o.onSelect(o)
            }}
            checked={o.checked}
            disabled={o.disabled}
            {...itemProps}
          >
            <DropdownMenu.ItemIndicator
              style={{
                position: 'absolute',
                display: 'inline-flex',
                left: 12,
              }}
            >
              <CheckCircle />
            </DropdownMenu.ItemIndicator>
            {!o.checked &&
              <div
                style={{
                  position: 'absolute',
                  display: 'inline-flex',
                  left: 12,
                }}
              >
                {o.icon}
              </div>
            }
            {o.label}
          </DropdownMenu.CheckboxItem>
        )
      case 'radio':
        return (
          <DropdownMenu.RadioItem
            key={o.id}
            className="poll-select-item"
            onSelect={(e: Event) => {
              e.stopPropagation()
              o.onSelect && o.onSelect(o)
            }}
            textValue={o.label}
            value={o.id}
            disabled={o.disabled}
            {...itemProps}
          >
            <DropdownMenu.ItemIndicator
              style={{
                position: 'absolute',
                display: 'inline-flex',
                left: 14,
              }}
            >
              <CheckCircle />
            </DropdownMenu.ItemIndicator>
            {o.label}
          </DropdownMenu.RadioItem>
        )
      case 'label':
        return (
          <DropdownMenu.Label key={o.id}>
            {o.label}
          </DropdownMenu.Label>
        )
      case 'node':
        return o.content
      case 'link':
        return (
          <a
            href={o.to ?? '/'}
            key={o.id}
            style={{ color: 'unset' }}
          >
            <DropdownMenu.Item
              className="poll-select-item"
              onSelect={(e: Event) => {
                e.stopPropagation()
                o.onSelect && o.onSelect(o)
              }}
              {...itemProps}
            >
              <div
                style={{
                  position: 'absolute',
                  display: 'inline-flex',
                  left: 14,
                }}
              >
                {o.icon}
              </div>
              {o.label}
            </DropdownMenu.Item>
          </a>
        )
      default:
        return (
          <DropdownMenu.Item
            key={o.id}
            className="poll-select-item"
            onSelect={(e: Event) => {
              e.stopPropagation()
              o.onSelect && o.onSelect(o)
            }}
            disabled={o.disabled}
            {...itemProps}
          >
            <div
              style={{
                position: 'absolute',
                display: 'inline-flex',
                left: 14,
              }}
            >
              {o.icon}
            </div>
            {o.label}
          </DropdownMenu.Item>
        )
    }
  }, [itemProps])

  return (
    <DropdownMenu.Root
      open={open && !disabled}
      onOpenChange={onOpenChange}
    >
      <DropdownMenu.Trigger
        asChild
        disabled={disabled}
      >
        {trigger}
      </DropdownMenu.Trigger>
      <DropdownMenu.Content
        className="poll-select-content"
        {...contentProps}
      >
        {optionGroups.map((group, i, a) => (
          <ConditionalWrapper
            condition={typeof group.type !== 'undefined'}
            wrapper={
              (children) => (group.type === 'radio'
                ? 
                <DropdownMenu.RadioGroup 
                  value={group.value}
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  {children}
                </DropdownMenu.RadioGroup>
                : 
                <DropdownMenu.Group 
                  style={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    gap: 2
                  }}
                >
                  {children}
                </DropdownMenu.Group>
              )
            }
            key={`${group.type}-${i}`}
          >
            {group.options.map(renderItem)}
            {i !== a.length - 1 && <Separator.Root key={`dropdown-separator-${i}`} className="poll-separator" />}
          </ConditionalWrapper>
        ))}
        {arrow && (
          <DropdownMenu.Arrow
            style={{
              fill: 'white',
              stroke: contentProps && contentProps.style && contentProps.style.borderColor ? contentProps.style.borderColor : 'var(--slate10)',
              strokeWidth: 2,
              transform: 'translateY(-1px)',
              strokeDasharray: 34,
              strokeDashoffset: -31,
              strokeLinejoin: 'miter',
              strokeMiterlimit: 12,
            }}
            offset={12}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  )
}

export default Dropdown